/**
 * I18nService - Lightweight internationalization for the game client
 * Singleton with dot-notation key lookup and {variable} interpolation.
 */

import { en } from './locales/en';
import { zhCN } from './locales/zh-CN';
import { ja } from './locales/ja';
import { ko } from './locales/ko';
import { fil } from './locales/fil';
import { EventBus } from '../utils/EventBus';
import { loadCJKFont } from './fontManager';

export type Locale = 'en' | 'zh-CN' | 'ja' | 'ko' | 'fil';

const LOCALE_STORAGE_KEY = 'crimson_mandate_locale';

const LOCALE_MAP: Record<Locale, Record<string, string>> = {
  'en': en,
  'zh-CN': zhCN,
  'ja': ja,
  'ko': ko,
  'fil': fil,
};

const CJK_LOCALES: Set<Locale> = new Set(['zh-CN', 'ja', 'ko']);

let currentLocale: Locale = 'en';
let currentStrings: Record<string, string> = en;

function detectLocale(): Locale {
  // Check localStorage first
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved && saved in LOCALE_MAP) return saved as Locale;
  } catch (_e) { /* localStorage unavailable */ }

  // Auto-detect from browser
  const nav = navigator.language || '';
  if (nav.startsWith('zh')) return 'zh-CN';
  if (nav.startsWith('ja')) return 'ja';
  if (nav.startsWith('ko')) return 'ko';
  if (nav.startsWith('fil') || nav.startsWith('tl')) return 'fil';

  return 'en';
}

export const I18n = {
  /**
   * Initialize i18n: detect language, load strings, load CJK font if needed.
   */
  init(): void {
    currentLocale = detectLocale();
    currentStrings = LOCALE_MAP[currentLocale] ?? en;
    if (CJK_LOCALES.has(currentLocale)) {
      loadCJKFont(currentLocale);
    }
    console.log(`[I18n] Initialized with locale: ${currentLocale}`);
  },

  /**
   * Translate a key with optional parameter interpolation.
   * Falls back to English, then to the raw key.
   */
  t(key: string, params?: Record<string, string | number>): string {
    let str = currentStrings[key] ?? en[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return str;
  },

  /**
   * Switch locale, persist to localStorage, emit event for UI refresh.
   */
  setLocale(locale: Locale): void {
    if (!(locale in LOCALE_MAP)) return;
    currentLocale = locale;
    currentStrings = LOCALE_MAP[locale] ?? en;
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch (_e) { /* localStorage unavailable */ }

    if (CJK_LOCALES.has(locale)) {
      loadCJKFont(locale);
    }

    EventBus.emit('locale-changed', { locale });
    console.log(`[I18n] Locale changed to: ${locale}`);
  },

  getLocale(): Locale {
    return currentLocale;
  },

  isCJK(): boolean {
    return CJK_LOCALES.has(currentLocale);
  },

  /** Get all available locales with display names */
  getAvailableLocales(): { code: Locale; name: string }[] {
    return [
      { code: 'en', name: 'English' },
      { code: 'zh-CN', name: '\u7B80\u4F53\u4E2D\u6587' },
      { code: 'ja', name: '\u65E5\u672C\u8A9E' },
      { code: 'ko', name: '\uD55C\uAD6D\uC5B4' },
      { code: 'fil', name: 'Filipino' },
    ];
  },

  /** Get localized ship display name from unit type ID (e.g. 'scout' → 'スカウト') */
  shipName(unitType: string): string {
    return currentStrings[`ship.${unitType}`] ?? en[`ship.${unitType}`] ?? unitType;
  },
};
